import { Button } from '@hbs-us/ui';
import { useCallback, useEffect, useId, useRef } from 'react';

/**
 * Accessible destructive-confirmation dialog (UI-SPEC Accessibility Contract:
 * focus-trapped, `aria-labelledby` its heading, returns focus on close, fully
 * keyboard-operable, Esc cancels). Replaces the legacy native `confirm()` with a
 * token-styled modal — every color is a locked `@hbs-us/ui` token (no hex).
 *
 * Built WITHOUT a new dependency (Registry Safety: no new top-level packages).
 * Focus is trapped by cycling Tab within the dialog; the backdrop click and Esc
 * both cancel; focus returns to the trigger on close.
 */

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

export function ConfirmDialog({
  open,
  heading,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  confirmPending,
  errorMessage,
  children,
}: {
  open: boolean;
  heading: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmPending?: boolean;
  /** Optional inline error (e.g. a server guard rejection) — keeps the dialog open. */
  errorMessage?: string | null;
  /**
   * Optional content between the body and the buttons, for the case where the useful action is
   * a CHOICE rather than yes/no — the clock screen lists the existing jobs at this address here,
   * so picking one is a tap instead of a cancel-and-retry. Anything focusable rendered here joins
   * the existing focus trap, which queries the live DOM rather than a fixed list.
   */
  children?: React.ReactNode;
}) {
  const headingId = useId();
  const bodyId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onCancel],
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const node = dialogRef.current;
    const firstFocusable = node?.querySelector<HTMLElement>(FOCUSABLE);
    firstFocusable?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    // Backdrop is presentational; dismissal is via Esc (handleKeyDown) or the
    // Cancel button (A11Y-01). No click-to-close on the static backdrop —
    // keyboard + button cover dismissal without a static-element handler.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={bodyId}
        onKeyDown={handleKeyDown}
        className="w-full max-w-md rounded-md border border-border bg-card p-6 shadow-lg"
      >
        <h2 id={headingId} className="font-display font-semibold text-foreground text-xl">
          {heading}
        </h2>
        <p id={bodyId} className="mt-2 text-muted-foreground text-sm">
          {body}
        </p>
        {children}
        {errorMessage ? (
          <p role="alert" aria-live="assertive" className="mt-3 text-destructive text-sm">
            {errorMessage}
          </p>
        ) : null}
        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={confirmPending}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
